<template>
  <button
    class="base-button-icon"
    :type="props.type"
    :class="classes"
    :disabled="props.disabled"
    :style="props.color ? `background-color: ${props.color}` : undefined"
    @click="handleClick"
    @pointerdown="handlePointerDown"
  >
    <slot v-if="!props.loading">
      <BaseIcon
        :name="props.iconName"
        :size="props.iconSize ?? iconSize"
        :color="props.iconColor"
        :fill-type="props.iconFillType"
        :stroke-width="props.iconStrokeWidth"
        :rotate="props.iconRotate"
      />
    </slot>
    <BaseLoader
      v-else
      size="28px"
      :color="colorLoader"
    />
  </button>
</template>

<script setup lang="ts">
import { playActionEffectTap } from '~/shared/lib'
import BaseIcon from '../icon/index.vue'
import BaseLoader from '../loader/index.vue'

interface Props {
  iconName: string
  iconFillType?: 'solid' | 'outline'
  iconStrokeWidth?: number
  iconSize?: string
  iconColor?: string
  iconRotate?: number
  form?: 'default' | 'circle'
  variant?:
    | 'primary'
    | 'secondary'
    | 'dark'
    | 'danger'
    | 'light'
    | 'transparent-inverted'
    | 'transparent'
    | 'success'
  size?: 'xxxs' | 'xxs' | 'xs' | 'sm' | 'md' | 'md-alt' | 'md-alt-2' | 'lg'
  color?: string
  type?: 'submit' | 'button'
  outlined?: boolean
  shadow?: boolean
  loading?: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  form: 'default',
  variant: 'secondary',
  size: 'md',
  type: 'button',
})

const emit = defineEmits<{
  (e: 'click', value: Event): void
}>()

const iconSize = computed(() => {
  switch (props.size) {
    case 'lg':
    case 'md':
    case 'md-alt-2':
    case 'sm':
      return '20px'

    case 'xs':
      return '18px'

    case 'xxs':
      return '14px'

    case 'xxxs':
      return '12px'

    default:
      return '24px'
  }
})

const classes = computed(() => ({
  [`base-button-icon--form-${props.form}`]: true,
  [`base-button-icon--variant-${props.variant}`]: true,
  [`base-button-icon--size-${props.size}`]: true,
  'base-button-icon--shadow': props.shadow,
  'base-button-icon--outlined': props.outlined,
}))

const colorLoader = computed(() => {
  switch (props.variant) {
    case 'light':
      return 'var(--color-foreground-main)'
    case 'secondary':
      return 'var(--color-foreground-regular)'
    case 'primary':
    case 'danger':
    case 'dark':
    case 'transparent-inverted':
    case 'success':
      return 'var(--color-foreground-inverted)'
    case 'transparent':
      return 'transparent'
    default:
      return ''
  }
})

function handlePointerDown(event: PointerEvent) {
  if (props.loading || props.disabled) {
    return
  }

  playActionEffectTap(event.currentTarget as HTMLButtonElement)
}

function handleClick(event: MouseEvent) {
  if (props.loading || props.disabled) {
    return
  }

  emit('click', event)
  ;(event.currentTarget as HTMLButtonElement).blur()
}
</script>

<style lang="scss" scoped>
.base-button-icon {
  @include drop-button-styles;

  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:disabled {
    opacity: .6;

    &:hover {
      transform: translate(0, 0);
    }
  }

  &--shadow {
    box-shadow: 0 0 12px 0 #3333;
  }

  &--variant-primary {
    @include action-effect-button(var(--color-surface-primary));

    &:deep(.iconify) {
      background-color: var(--color-foreground-light);
    }

    &.base-button-icon--outlined {
      border: 2px solid var(--color-foreground-primary);
      background-color: var(--color-surface-bg);

      &:deep(.iconify) {
        background-color: var(--color-foreground-primary);
      }
    }
  }

  &--variant-primary.base-button-icon--with-overlay-circle::after {
    border-color: red !important;
    opacity: 0.3 !important;

    &:deep(.iconify) {
      background-color: var(--color-foreground-primary);
    }
  }

  &--variant-secondary {
    @include action-effect-button(var(--color-surface-placeholder-600));
  }

  &--variant-dark {
    @include action-effect-button(var(--color-surface-inverted-900));

    &:deep(.iconify) {
      background-color: var(--color-foreground-inverted);
    }
  }

  &--variant-danger {
    @include action-effect-button(var(--color-surface-danger));

    &:deep(.iconify) {
      background-color: var(--color-foreground-light);
    }
  }

  &--variant-light {
    @include action-effect-button(var(--color-surface-bg));
  }

  &--variant-transparent-inverted {
    @include action-effect-button(var(--color-surface-transparent-inverted));
  }

  &--variant-transparent {
    background-color: transparent;

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        transform: none !important;
        box-shadow: none !important;
        opacity: 0.72;
      }
    }

    &:active {
      transform: none !important;
      opacity: 0.55;
    }
  }

  &--variant-success {
    @include action-effect-button(var(--color-surface-success));
  }

  &--size-lg {
    width: 48px;
    height: 48px;

    @include bp-media('xs', 'sm') {
      width: 36px;
      height: 36px;
    }
  }

  &--size-md {
    width: 42px;
    height: 42px;

    @include bp-media('xs', 'sm') {
      width: 36px;
      height: 36px;
    }
  }

  &--size-md-alt {
    width: 40px;
    height: 40px;
  }

  &--size-md-alt-2 {
    width: 38px;
    height: 38px;
  }

  &--size-sm {
    width: 34px;
    height: 34px;
  }

  &--size-xs {
    width: 28px;
    height: 28px;
    border-radius: rem(8px) !important;
  }

  &--size-xxs {
    width: 22px;
    height: 22px;
    border-radius: rem(8px);
  }

  &--size-xxxs {
    width: 16px;
    height: 16px;
  }

  &--form-default {
    border-radius: var(--radius-xs);
  }

  &--form-circle {
    border-radius: 48px;
  }
}
</style>
